import { styled } from '@mui/material/styles'
import {
  Box,
  FormControl,
  Grid,
  InputBase,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { typedEntries } from '@/utils/typed-entries.ts'
import CheckIcon from '@mui/icons-material/Check'
import { blackVariants } from '@/themes/colors'

const SelectedBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.contrastText
}))

const StyledInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3)
  },
  '& .MuiInputBase-input': {
    borderRadius: theme.shape.borderRadius,
    border: '1px solid',
    borderColor: 'theme.palette.grey[600]',
    fontSize: theme.typography.pxToRem(12),
    padding: theme.spacing(0.5, 1),
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    // Use the system font instead of the default Roboto font.
    fontFamily: ['Geist Mono', 'mono'].join(','),
    '&:focus': {
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)'
    }
  }
}))

export interface OptionProps {
  label: string
}

export interface FilterChipProps<T extends string> {
  label: string
  selected?: T[]
  options: Partial<Record<T, OptionProps>>
  multiple?: boolean
  onFiltersSelected?: (filter: T[]) => void
}

export function FilterChip<T extends string>({
  label,
  selected: selectedProp,
  options,
  multiple = true,
  onFiltersSelected = () => {}
}: FilterChipProps<T>) {
  const [selected, setSelected] = useState<T[]>(selectedProp ?? [])

  const handleChange = (event: SelectChangeEvent<T[]>) => {
    const {
      target: { value }
    } = event

    const selected = typeof value === 'string' ? (value.split(',') as T[]) : value
    setSelected(selected)
    onFiltersSelected(selected)
  }

  return (
    <FormControl sx={{ color: 'grey.600' }}>
      <Select
        labelId="demo-multiple-chip-label"
        id="demo-multiple-chip"
        multiple={multiple}
        displayEmpty
        value={selected}
        onChange={handleChange}
        input={<StyledInput id="select-multiple-chip" />}
        renderValue={selected => (
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {selected.length ? (
              <SelectedBox sx={{ mr: 1 }}>{selected.length}</SelectedBox>
            ) : undefined}
            <Typography variant={'body2'} color={blackVariants['600']}>
              {label}
            </Typography>
          </Box>
        )}
      >
        {typedEntries(options).map(([key, props]) => (
          <MenuItem key={key} value={key} selected={selected.includes(key)}>
            <Grid container sx={{ width: '100%', justifyContent: 'space-between' }}>
              <Grid size={10}>{props.label}</Grid>
              <Grid size={2}>
                {selected.includes(key) && <CheckIcon sx={{ float: 'right' }} />}
              </Grid>
            </Grid>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
